import type {
  DeadlineAiProposal,
  DeadlineAiTypeTerms,
} from '../dto/deadline/deadline-ai.constants';
import type { DeadlineRecurrenceChoice } from '../dto/deadline/deadline-management.constants';
import { buildDeadlineAiSuggestion, readConfidentValue } from './deadline-ai-suggestion';

/** Tipo del catalogo con lo slug che la lettura restituisce. */
export interface DeadlineAiCatalogType extends DeadlineAiTypeTerms {
  slug: string;
}

/** Campi che la lettura può proporre; `null` = non proposto (assente o sotto la soglia di confidenza). */
export interface DeadlineAiFieldProposal {
  /** Solo quando il tipo viene dall'IA: un tipo scelto dall'utente prevale e non si ripropone. */
  documentTypeId: string | null;
  recurrence: DeadlineRecurrenceChoice | null;
  issuedAt: string | null;
  dueAt: string | null;
  validityMonths: number | null;
}

export interface BuildDeadlineAiFieldProposalInput {
  proposal: DeadlineAiProposal;
  /** Tipi selezionabili (attivi): lo slug letto si risolve solo fra questi. */
  documentTypes: readonly DeadlineAiCatalogType[];
  /** Tipo già scelto dall'utente, se c'è. */
  chosenTypeId: string | null;
  todayIso: string;
}

function findAiType(
  proposal: DeadlineAiProposal,
  documentTypes: readonly DeadlineAiCatalogType[],
): DeadlineAiCatalogType | null {
  const aiSlug = readConfidentValue(proposal.documentTypeSlug);
  return (aiSlug && documentTypes.find((documentType) => documentType.slug === aiSlug)) || null;
}

function findChosenType(
  documentTypes: readonly DeadlineAiCatalogType[],
  chosenTypeId: string | null,
): DeadlineAiCatalogType | null {
  return (
    (chosenTypeId && documentTypes.find((documentType) => documentType.id === chosenTypeId)) || null
  );
}

/**
 * Dalla lettura dell'IA ai valori dei campi (dialog di creazione e assistente): stesse regole del
 * server per tipo, ricorrenza e scadenza; una scadenza già passata non è proponibile.
 */
export function buildDeadlineAiFieldProposal(
  input: BuildDeadlineAiFieldProposalInput,
): DeadlineAiFieldProposal {
  const suggestion = buildDeadlineAiSuggestion({
    proposal: input.proposal,
    aiType: findAiType(input.proposal, input.documentTypes),
    documentType: findChosenType(input.documentTypes, input.chosenTypeId),
    linked: null,
  });
  const dueAtIsUpcoming = suggestion.dueAt !== null && suggestion.dueAt >= input.todayIso;
  return {
    documentTypeId: suggestion.typeSource === 'AI' ? suggestion.documentTypeId : null,
    recurrence: suggestion.recurrence,
    issuedAt: suggestion.issuedAt,
    dueAt: dueAtIsUpcoming ? suggestion.dueAt : null,
    validityMonths: suggestion.validityMonths,
  };
}
